import React, { useState } from 'react';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
    Alert,
    AlertDescription,
    AlertTitle,
    Badge,
    Breadcrumb,
    BreadcrumbEllipsis,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
    Button,
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
    Checkbox,
    DataList,
    DataItem,
    DataLabel,
    DataValue,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuShortcut,
    DropdownMenuTrigger,
    FeatureCard,
    Input,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    RadioGroup,
    RadioGroupItem,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Slider,
    Spinner,
    StatusIndicator,
    Switch,
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableFooter,
    TableHead,
    TableHeader,
    TableRow,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    Textarea,
    Toast,
    ToastAction,
    ToastDescription,
    ToastProvider,
    ToastTitle,
    ToastViewport,
    Tooltip,
    TooltipProvider,
    type ToastPosition,
    MobileSheet,
} from '../../ui';
import { faCoffee, faArrowRight, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// interactive code block with copy functionality
const CodeBlock = ({ code, title }: { code: string; title?: string }) => {
    const [copied, setCopied] = useState(false);

    const copyToClipboard = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="mt-4 space-y-2">
            {title && <p className="text-sm font-medium text-foreground">{title}</p>}
            <div className="relative">
                <div className="p-4 bg-ink text-paper rounded-sm border-brutal border-ink shadow-brutal-sm overflow-x-auto">
                    <pre className="text-sm font-mono">
                        <code>{code}</code>
                    </pre>
                </div>
                <button
                    onClick={copyToClipboard}
                    className="absolute top-2 right-2 p-2 bg-mint/20 hover:bg-mint/40 rounded-sm border border-ink transition-colors"
                    title="Copy code"
                >
                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
};

// section wrapper with description
const Section = ({
    title,
    description,
    children,
    codeExample,
    codeTitle
}: {
    title: string;
    description?: string;
    children: React.ReactNode;
    codeExample?: string;
    codeTitle?: string;
}) => (
    <section className="space-y-4">
        <div>
            <h2 className="text-2xl font-semibold text-foreground">{title}</h2>
            {description && (
                <p className="mt-2 text-sm text-muted-foreground max-w-3xl">
                    {description}
                </p>
            )}
        </div>
        <div className="space-y-4">
            {children}
        </div>
        {codeExample && <CodeBlock code={codeExample} title={codeTitle} />}
    </section>
);

const StyleGuide = () => {
    // interactive state
    const [inputValue, setInputValue] = useState("");
    const [selectedTab, setSelectedTab] = useState("account");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [buttonLoading, setButtonLoading] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState("generating");
    const [customColor, setCustomColor] = useState("#9b59b6");
    const [switchChecked, setSwitchChecked] = useState(false);
    const [checkboxChecked, setCheckboxChecked] = useState(false);
    const [radioValue, setRadioValue] = useState("option1");
    const [sliderValue, setSliderValue] = useState([50]);
    const [textareaValue, setTextareaValue] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [toasts, setToasts] = useState<Array<{ id: string; title: string; description?: string; variant?: "default" | "info" | "success" | "warning" | "error"; open: boolean }>>([]);
    const [toastPosition, setToastPosition] = useState<ToastPosition>("bottom-right");
    // mobile sheet preview state
    const [defaultSheetOpen, setDefaultSheetOpen] = useState(false);
    const [fullModalSheetOpen, setFullModalSheetOpen] = useState(false);

    const handleLoadingDemo = () => {
        setButtonLoading(true);
        setTimeout(() => setButtonLoading(false), 2000);
    };

    return (
        <div className="container mx-auto p-8 space-y-12 bg-background min-h-screen">
            <div className="space-y-4">
                <h1 className="text-4xl font-bold text-foreground">UI Component Styleguide</h1>
                <p className="text-xl text-muted-foreground max-w-3xl">
                    A comprehensive collection of reusable components for n450s, built with a neo-brutalist design system.
                    All components use shared design tokens for consistent styling across the application.
                </p>
            </div>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Buttons */}
            <Section
                title="Buttons"
                description="Buttons are the primary interactive elements. They support multiple variants, sizes, icons, and custom colors. All buttons use the brutal border and shadow system for consistency."
                codeExample={`// standard button
<Button>Click me</Button>

// variants
<Button variant="default">Default (Mint)</Button>
<Button variant="sky">Sky Blue</Button>
<Button variant="lemon">Lemon Yellow</Button>
<Button variant="destructive">Destructive (Pink)</Button>
<Button variant="outline">Outline</Button>
<Button variant="ghost">Ghost</Button>
<Button variant="link">Link</Button>

// with icons
<Button icon={faCoffee} iconPosition="left">Coffee</Button>
<Button icon={faArrowRight} iconPosition="right">Continue</Button>

// loading state
<Button loading>Processing...</Button>

// custom colors (works on ANY variant!)
<Button variant="default" bgColor="#9b59b6" textColor="white">
  Custom Default
</Button>
<Button variant="outline" bgColor="#9b59b6" textColor="white">
  Custom Outline
</Button>

// sizes
<Button size="sm">Small</Button>
<Button size="default">Default</Button>
<Button size="lg">Large</Button>`}
                codeTitle="Button Examples"
            >
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-medium mb-3">Variants</h3>
                        <div className="flex flex-wrap gap-4 items-center">
                            <Button>Default</Button>
                            <Button variant="sky">Sky</Button>
                            <Button variant="lemon">Lemon</Button>
                            <Button variant="secondary">Secondary (Gold)</Button>
                            <Button variant="destructive">Destructive</Button>
                            <Button variant="outline">Outline</Button>
                            <Button variant="ghost">Ghost</Button>
                            <Button variant="link">Link</Button>
                            <Button disabled>Disabled</Button>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-medium mb-3">With Icons</h3>
                        <div className="flex flex-wrap gap-4 items-center">
                            <Button icon={faCoffee}>Left Icon</Button>
                            <Button icon={faArrowRight} iconPosition="right">Right Icon</Button>
                            <Button icon={faCoffee} iconPosition="left" variant="sky">Sky with Icon</Button>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-medium mb-3">Sizes</h3>
                        <div className="flex flex-wrap gap-4 items-center">
                            <Button size="sm">Small</Button>
                            <Button size="default">Default</Button>
                            <Button size="lg">Large</Button>
                            <Button size="icon" icon={faCoffee} />
                        </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-medium mb-3">Interactive States</h3>
                        <div className="flex flex-wrap gap-4 items-center">
                            <Button loading={buttonLoading} onClick={handleLoadingDemo}>
                                {buttonLoading ? "Loading..." : "Click to Load"}
                            </Button>
                            <Button variant="default" bgColor={customColor} textColor="white">
                                Custom Default
                            </Button>
                            <Button variant="outline" bgColor={customColor} textColor="white">
                                Custom Outline
                            </Button>
                            <div className="flex items-center gap-2">
                                <label className="text-sm">Color:</label>
                                <input
                                    type="color"
                                    value={customColor}
                                    onChange={(e) => setCustomColor(e.target.value)}
                                    className="h-8 w-16 border border-ink rounded-sm cursor-pointer"
                                />
                            </div>
                        </div>
                    </div>
                    <div>
                        <h3 className="text-lg font-medium mb-3">All Variants Support Custom Colors</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                            You can pass bgColor, hoverBgColor, textColor, and borderColor to any variant.
                            If textColor is not provided, it auto-selects black or white based on contrast.
                        </p>
                        <div className="flex flex-wrap gap-4 items-center">
                            <Button variant="default" bgColor="#9b59b6">Purple Default</Button>
                            <Button variant="sky" bgColor="#e74c3c">Red Sky</Button>
                            <Button variant="outline" bgColor="#2ecc71" textColor="white">Green Outline</Button>
                            <Button variant="ghost" bgColor="#f39c12" textColor="white">Orange Ghost</Button>
                        </div>
                    </div>
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Inputs */}
            <Section
                title="Inputs"
                description="Text input fields with brutal styling. They feature inset shadows and hover effects that match the site's design system."
                codeExample={`<Input type="text" placeholder="Enter text..." />
<Input type="email" placeholder="email@example.com" />
<Input type="password" placeholder="Password" />
<Input disabled placeholder="Disabled input" />`}
            >
                <div className="w-full max-w-md space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-2">Controlled Input</label>
                        <Input
                            type="text"
                            placeholder="Type something..."
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                        />
                        {inputValue && (
                            <p className="mt-2 text-sm text-muted-foreground">
                                You typed: <strong>{inputValue}</strong>
                            </p>
                        )}
                    </div>
                    <Input type="email" placeholder="Email address" />
                    <Input type="password" placeholder="Password" />
                    <Input disabled placeholder="Disabled input" />
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Feature Cards */}
            <Section
                title="Feature Cards"
                description="Bold, colorful cards used for highlighting features on the home page. They use the brutal shadow system and support multiple variants for different emphasis levels."
                codeExample={`<FeatureCard variant="default">
  <p>Default yellow card</p>
</FeatureCard>

<FeatureCard variant="purple">
  <p>Purple variant</p>
</FeatureCard>

<FeatureCard variant="important">
  <p>IMPORTANT - Thick border with glow</p>
</FeatureCard>

<FeatureCard variant="neonPink">
  <p>NEON PINK - Alternative high visibility</p>
</FeatureCard>

// fully custom
<FeatureCard style={{ backgroundColor: '#ff9f43' }}>
  Custom styled card
</FeatureCard>`}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <FeatureCard>
                        <p className="font-bold mb-2">Default Feature Card</p>
                        <p className="text-sm">Standard yellow box used on the home page for general features.</p>
                    </FeatureCard>
                    <FeatureCard variant="purple">
                        <p className="font-bold mb-2">Purple Variant</p>
                        <p className="text-sm">Used for secondary features or alternative highlights.</p>
                    </FeatureCard>
                    <FeatureCard variant="important">
                        <p className="font-bold mb-2">IMPORTANT BOX</p>
                        <p className="text-base font-normal">High visibility with thick border and glow effect. Use for critical information.</p>
                    </FeatureCard>
                    <FeatureCard variant="neonPink">
                        <p className="font-bold mb-2">NEON PINK BOX</p>
                        <p className="text-base font-normal">Alternative high visibility style with pink glow.</p>
                    </FeatureCard>
                    <FeatureCard variant="default" style={{ backgroundColor: '#ff9f43', borderColor: '#e67e22' }}>
                        <p className="font-bold mb-2">Fully Custom Box</p>
                        <p className="text-sm">Passed via `style` prop for complete customization.</p>
                    </FeatureCard>
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Cards */}
            <Section
                title="Cards"
                description="Generic card container with header, content, and footer sections. Perfect for grouping related content like profiles, forms, or information panels."
                codeExample={`<Card>
  <CardHeader>
    <CardTitle>Card Title</CardTitle>
    <CardDescription>Description text</CardDescription>
  </CardHeader>
  <CardContent>
    <p>Main content goes here</p>
  </CardContent>
  <CardFooter>
    <Button>Action</Button>
  </CardFooter>
</Card>`}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Card Title</CardTitle>
                            <CardDescription>Card Description goes here. This is a generic card container.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <p className="text-sm">Card Content. This can be used for profiles, forms, or other grouped content.</p>
                        </CardContent>
                        <CardFooter>
                            <Button>Action</Button>
                        </CardFooter>
                    </Card>
                    <Card>
                        <CardHeader>
                            <CardTitle>Minimal Card</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-sm">Cards can have just content without footer or description.</p>
                        </CardContent>
                    </Card>
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Data Display */}
            <Section
                title="Data Display"
                description="Components for displaying structured data. The 'box' variant matches the profile page styling with inset shadows and proper word-break handling."
                codeExample={`// box variant (matches profile-box styling)
<DataList variant="box">
  <DataItem variant="box">
    <DataLabel>username:</DataLabel>
    <DataValue>nassos</DataValue>
  </DataItem>
</DataList>

// default list variant
<DataList>
  <DataItem>
    <DataLabel>Field:</DataLabel>
    <DataValue>Value</DataValue>
  </DataItem>
</DataList>`}
            >
                <div className="space-y-8">
                    <div>
                        <h3 className="text-lg font-medium mb-3">Box Variant (Profile Page Style)</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            This variant matches the exact styling from the profile page, including padding, margins, borders, and inset shadows.
                        </p>
                        <DataList variant="box">
                            <DataItem variant="box">
                                <DataLabel>username:</DataLabel>
                                <DataValue>nassos</DataValue>
                            </DataItem>
                            <DataItem variant="box">
                                <DataLabel>role:</DataLabel>
                                <DataValue>Admin</DataValue>
                            </DataItem>
                            <DataItem variant="box">
                                <DataLabel>status:</DataLabel>
                                <DataValue>Active</DataValue>
                            </DataItem>
                            <DataItem variant="box">
                                <DataLabel>tokens:</DataLabel>
                                <DataValue>1,234</DataValue>
                            </DataItem>
                        </DataList>
                    </div>

                    <div className="max-w-md">
                        <h3 className="text-lg font-medium mb-3">Default List Variant</h3>
                        <Card>
                            <CardContent className="pt-6">
                                <DataList>
                                    <DataItem>
                                        <DataLabel>Username:</DataLabel>
                                        <DataValue>nassos</DataValue>
                                    </DataItem>
                                    <DataItem>
                                        <DataLabel>Role:</DataLabel>
                                        <DataValue>Admin</DataValue>
                                    </DataItem>
                                    <DataItem>
                                        <DataLabel>Email:</DataLabel>
                                        <DataValue>user@example.com</DataValue>
                                    </DataItem>
                                </DataList>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Status Indicators */}
            <Section
                title="Status Indicators"
                description="Animated status indicators with blinking text effects. The letters animate individually with a gradient that transitions from gray to the status color. Matches the AOTD status indicator styling exactly."
                codeExample={`// overlay mode (default)
<StatusIndicator status="generating" />

// inline mode
<StatusIndicator status="loading" overlay={false} />

// with custom message
<StatusIndicator 
  status="custom" 
  message="Processing your request..." 
  overlay={false} 
/>

// available statuses: completed, generating, busy, resuming, pending, started, loading, failed, error`}
            >
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-medium mb-3">With Overlay (Default)</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            Overlay mode positions the indicator absolutely in the bottom-left corner, typically over images or content.
                        </p>
                        <div className="relative w-64 h-32 border-2 border-dashed border-ink rounded-sm bg-muted/20 flex items-center justify-center">
                            <span className="text-muted-foreground">Image/Content Placeholder</span>
                            <StatusIndicator status={selectedStatus} />
                        </div>
                        <div className="mt-4 flex gap-2 flex-wrap">
                            {['generating', 'loading', 'completed', 'failed', 'pending'].map((status) => (
                                <Button
                                    key={status}
                                    size="sm"
                                    variant={selectedStatus === status ? "default" : "outline"}
                                    onClick={() => setSelectedStatus(status)}
                                >
                                    {status}
                                </Button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-medium mb-3">Inline / No Overlay</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            Inline mode displays the indicator in the normal document flow, useful for lists or status pages.
                        </p>
                        <div className="flex flex-col gap-4">
                            <StatusIndicator status="loading" overlay={false} showDot={true} />
                            <StatusIndicator status="generating" overlay={false} showDot={true} />
                            <StatusIndicator status="completed" overlay={false} showDot={true} />
                            <StatusIndicator status="failed" overlay={false} showDot={true} />
                            <StatusIndicator status="pending" overlay={false} showDot={true} />
                            <StatusIndicator
                                status="custom"
                                message="Custom status message with blinking letters"
                                overlay={false}
                                showDot={true}
                            />
                        </div>
                    </div>
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Badges */}
            <Section
                title="Badges"
                description="Small status indicators and labels. Use badges to highlight status, categories, or tags. They support multiple color variants matching the site's palette."
                codeExample={`<Badge>Default</Badge>
<Badge variant="secondary">Secondary</Badge>
<Badge variant="destructive">Destructive</Badge>
<Badge variant="outline">Outline</Badge>
<Badge variant="success">Success</Badge>
<Badge variant="warning">Warning</Badge>`}
            >
                <div className="flex flex-wrap gap-3 items-center">
                    <Badge>Default</Badge>
                    <Badge variant="secondary">Secondary</Badge>
                    <Badge variant="outline">Outline</Badge>
                    <Badge variant="destructive">Destructive</Badge>
                    <Badge variant="success">Success</Badge>
                    <Badge variant="warning">Warning</Badge>
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Tabs */}
            <Section
                title="Tabs"
                description="Tab navigation component for organizing content into multiple panels. The active tab has a bottom border and background highlight."
                codeExample={`<Tabs defaultValue="account">
  <TabsList>
    <TabsTrigger value="account">Account</TabsTrigger>
    <TabsTrigger value="password">Password</TabsTrigger>
  </TabsList>
  <TabsContent value="account">
    Account content here
  </TabsContent>
  <TabsContent value="password">
    Password content here
  </TabsContent>
</Tabs>`}
            >
                <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full max-w-2xl">
                    <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="account">Account</TabsTrigger>
                        <TabsTrigger value="password">Password</TabsTrigger>
                        <TabsTrigger value="settings">Settings</TabsTrigger>
                    </TabsList>
                    <TabsContent value="account">
                        <Card>
                            <CardHeader>
                                <CardTitle>Account</CardTitle>
                                <CardDescription>
                                    Make changes to your account here. Click save when you're done.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <p className="text-sm">Account settings content (Active Tab)</p>
                                <Input placeholder="Account name" />
                            </CardContent>
                            <CardFooter>
                                <Button>Save changes</Button>
                            </CardFooter>
                        </Card>
                    </TabsContent>
                    <TabsContent value="password">
                        <Card>
                            <CardHeader>
                                <CardTitle>Password</CardTitle>
                                <CardDescription>
                                    Update your password here.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <Input type="password" placeholder="Current password" />
                                <Input type="password" placeholder="New password" />
                            </CardContent>
                            <CardFooter>
                                <Button>Update password</Button>
                            </CardFooter>
                        </Card>
                    </TabsContent>
                    <TabsContent value="settings">
                        <Card>
                            <CardContent className="pt-6">
                                <p className="text-sm">Settings content goes here.</p>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Dialog */}
            <Section
                title="Dialog"
                description="Modal dialogs for important interactions. Dialogs use the brutal shadow system and include a close button. They overlay the entire screen with a dark backdrop."
                codeExample={`<Dialog>
  <DialogTrigger asChild>
    <Button>Open Dialog</Button>
  </DialogTrigger>
  <DialogContent>
    <DialogHeader>
      <DialogTitle>Edit profile</DialogTitle>
      <DialogDescription>
        Make changes to your profile here.
      </DialogDescription>
    </DialogHeader>
    <div className="grid gap-4 py-4">
      <Input placeholder="Name" />
    </div>
    <DialogFooter>
      <Button type="submit">Save changes</Button>
    </DialogFooter>
  </DialogContent>
</Dialog>`}
            >
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button variant="outline">Open Dialog</Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                        <DialogHeader>
                            <DialogTitle>Edit profile</DialogTitle>
                            <DialogDescription>
                                Make changes to your profile here. Click save when you're done.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                                <label className="text-right text-sm">Name</label>
                                <Input placeholder="Pedro Duarte" className="col-span-3" />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <label className="text-right text-sm">Email</label>
                                <Input type="email" placeholder="pedro@example.com" className="col-span-3" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" onClick={() => setDialogOpen(false)}>
                                Save changes
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Mobile Sheet */}
            <Section
                title="Mobile Sheet"
                description="Bottom sheet component for mobile interfaces. By default, it's non-modal so the bottom navigation bar remains clickable. Use fullModal prop to create a blocking modal that covers everything including the bottom bar. The overlay has a dark background with slight blur."
                codeExample={`// default mode (non-modal - bottom bar stays clickable)
<MobileSheet
  open={open}
  onOpenChange={setOpen}
  maxHeight="80vh"
>
  <div>Sheet content</div>
</MobileSheet>

// full modal mode (blocks everything)
<MobileSheet
  open={open}
  onOpenChange={setOpen}
  fullModal={true}
  maxHeight="80vh"
>
  <div>Sheet content</div>
</MobileSheet>`}
            >
                <div className="space-y-4">
                    <div className="flex flex-wrap gap-4">
                        <Button
                            variant="outline"
                            onClick={() => setDefaultSheetOpen(true)}
                        >
                            Open MobileSheet (Default / Non-Modal)
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setFullModalSheetOpen(true)}
                        >
                            Open MobileSheet (Full Modal)
                        </Button>
                    </div>

                    {/* default sheet */}
                    <MobileSheet
                        open={defaultSheetOpen}
                        onOpenChange={setDefaultSheetOpen}
                        maxHeight="60vh"
                    >
                        <div className="space-y-4 py-4">
                            <div>
                                <h3 className="text-lg font-semibold mb-2">Default Mode (Non-Modal)</h3>
                                <p className="text-sm text-muted-foreground mb-4">
                                    This sheet is non-modal. The bottom navigation bar remains clickable when this sheet is open.
                                    The overlay doesn't cover the bottom bar area.
                                </p>
                            </div>
                            <Button
                                variant="default"
                                onClick={() => setDefaultSheetOpen(false)}
                                className="w-full"
                            >
                                Close Sheet
                            </Button>
                        </div>
                    </MobileSheet>

                    {/* full modal sheet */}
                    <MobileSheet
                        open={fullModalSheetOpen}
                        onOpenChange={setFullModalSheetOpen}
                        fullModal={true}
                        maxHeight="60vh"
                    >
                        <div className="space-y-4 py-4">
                            <div>
                                <h3 className="text-lg font-semibold mb-2">Full Modal Mode</h3>
                                <p className="text-sm text-muted-foreground mb-4">
                                    This sheet is in full modal mode. The overlay covers the entire viewport including the bottom bar,
                                    and all interactions outside the sheet are blocked.
                                </p>
                            </div>
                            <Button
                                variant="default"
                                onClick={() => setFullModalSheetOpen(false)}
                                className="w-full"
                            >
                                Close Sheet
                            </Button>
                        </div>
                    </MobileSheet>
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Dropdown Menu */}
            <Section
                title="Dropdown Menu"
                description="Context menus that appear on click. Dropdown menus support separators, shortcuts, groups, and sub-menus. Items have hover effects matching the site's mint accent color."
                codeExample={`<DropdownMenu>
  <DropdownMenuTrigger asChild>
    <Button>Open Menu</Button>
  </DropdownMenuTrigger>
  <DropdownMenuContent>
    <DropdownMenuLabel>My Account</DropdownMenuLabel>
    <DropdownMenuSeparator />
    <DropdownMenuItem>
      Profile
      <DropdownMenuShortcut>⇧⌘P</DropdownMenuShortcut>
    </DropdownMenuItem>
    <DropdownMenuItem>Settings</DropdownMenuItem>
  </DropdownMenuContent>
</DropdownMenu>`}
            >
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline">Open Menu</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56">
                        <DropdownMenuLabel>My Account</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuGroup>
                            <DropdownMenuItem>
                                Profile
                                <DropdownMenuShortcut>⇧⌘P</DropdownMenuShortcut>
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                                Billing
                                <DropdownMenuShortcut>⌘B</DropdownMenuShortcut>
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                                Settings
                                <DropdownMenuShortcut>⌘S</DropdownMenuShortcut>
                            </DropdownMenuItem>
                        </DropdownMenuGroup>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem>
                            Log out
                            <DropdownMenuShortcut>⇧⌘Q</DropdownMenuShortcut>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Tooltip */}
            <Section
                title="Tooltip"
                description="Hover tooltips that provide additional context. Tooltips use the brutal border and shadow system for consistency with other components. Use the convenience wrapper with a content prop, or use the lower-level components for more control."
                codeExample={`// convenience wrapper (recommended)
<Tooltip content="Tooltip text">
  <Button>Hover me</Button>
</Tooltip>

// or use lower-level components
<TooltipProvider>
  <TooltipRoot>
    <TooltipTrigger asChild>
      <Button>Hover me</Button>
    </TooltipTrigger>
    <TooltipContent>
      <p>Tooltip text</p>
    </TooltipContent>
  </TooltipRoot>
</TooltipProvider>`}
            >
                <TooltipProvider>
                    <div className="flex flex-wrap gap-4">
                        <Tooltip content="This is a tooltip with brutal styling!">
                            <Button variant="outline">Hover me</Button>
                        </Tooltip>
                        <Tooltip content="Tooltips work on any element">
                            <Button variant="sky">Another button</Button>
                        </Tooltip>
                        <Tooltip content="Even on badges!">
                            <Badge>Hover badge</Badge>
                        </Tooltip>
                    </div>
                </TooltipProvider>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Select */}
            <Section
                title="Select"
                description="Dropdown select component for choosing from a list of options. Matches the Input component styling with brutal borders and inset shadows."
                codeExample={`<Select>
  <SelectTrigger>
    <SelectValue placeholder="Select an option" />
  </SelectTrigger>
  <SelectContent>
    <SelectItem value="option1">Option 1</SelectItem>
    <SelectItem value="option2">Option 2</SelectItem>
    <SelectItem value="option3">Option 3</SelectItem>
  </SelectContent>
</Select>`}
            >
                <div className="w-full max-w-sm space-y-4">
                    <Select>
                        <SelectTrigger>
                            <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="option1">Option 1</SelectItem>
                            <SelectItem value="option2">Option 2</SelectItem>
                            <SelectItem value="option3">Option 3</SelectItem>
                            <SelectItem value="option4">Option 4</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select>
                        <SelectTrigger>
                            <SelectValue placeholder="Choose a color" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="red">Red</SelectItem>
                            <SelectItem value="blue">Blue</SelectItem>
                            <SelectItem value="green">Green</SelectItem>
                            <SelectItem value="yellow">Yellow</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Switch */}
            <Section
                title="Switch"
                description="Toggle switch component for on/off states. Uses brutal styling with inset shadows and mint accent when checked."
                codeExample={`<Switch checked={checked} onCheckedChange={setChecked} />
<Switch defaultChecked disabled />`}
            >
                <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-3">
                        <Switch checked={switchChecked} onCheckedChange={setSwitchChecked} id="switch-demo" />
                        <label htmlFor="switch-demo" className="text-sm">
                            {switchChecked ? "On" : "Off"}
                        </label>
                    </div>
                    <Switch defaultChecked disabled />
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Checkbox */}
            <Section
                title="Checkbox"
                description="Checkbox input for single or multiple selections. Features brutal border and mint fill when checked."
                codeExample={`<Checkbox checked={checked} onCheckedChange={setChecked} />
<Checkbox defaultChecked disabled />`}
            >
                <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-3">
                        <Checkbox
                            checked={checkboxChecked}
                            onCheckedChange={(checked) => setCheckboxChecked(checked === true)}
                            id="checkbox-demo"
                        />
                        <label htmlFor="checkbox-demo" className="text-sm">
                            Accept terms and conditions
                        </label>
                    </div>
                    <div className="flex items-center gap-3">
                        <Checkbox defaultChecked id="checkbox-default" />
                        <label htmlFor="checkbox-default" className="text-sm">
                            Default checked
                        </label>
                    </div>
                    <div className="flex items-center gap-3">
                        <Checkbox disabled id="checkbox-disabled" />
                        <label htmlFor="checkbox-disabled" className="text-sm text-muted-foreground">
                            Disabled
                        </label>
                    </div>
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* RadioGroup */}
            <Section
                title="Radio Group"
                description="Radio button group for single selection from multiple options. Only one option can be selected at a time."
                codeExample={`<RadioGroup value={value} onValueChange={setValue}>
  <div className="flex items-center gap-2">
    <RadioGroupItem value="option1" id="r1" />
    <label htmlFor="r1">Option 1</label>
  </div>
  <div className="flex items-center gap-2">
    <RadioGroupItem value="option2" id="r2" />
    <label htmlFor="r2">Option 2</label>
  </div>
</RadioGroup>`}
            >
                <RadioGroup value={radioValue} onValueChange={setRadioValue}>
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                            <RadioGroupItem value="option1" id="radio1" />
                            <label htmlFor="radio1" className="text-sm cursor-pointer">Option 1</label>
                        </div>
                        <div className="flex items-center gap-2">
                            <RadioGroupItem value="option2" id="radio2" />
                            <label htmlFor="radio2" className="text-sm cursor-pointer">Option 2</label>
                        </div>
                        <div className="flex items-center gap-2">
                            <RadioGroupItem value="option3" id="radio3" />
                            <label htmlFor="radio3" className="text-sm cursor-pointer">Option 3</label>
                        </div>
                    </div>
                </RadioGroup>
                <p className="mt-2 text-sm text-muted-foreground">
                    Selected: <strong>{radioValue}</strong>
                </p>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Textarea */}
            <Section
                title="Textarea"
                description="Multi-line text input matching Input styling. Perfect for longer form fields like comments or descriptions."
                codeExample={`<Textarea placeholder="Enter your message..." />
<Textarea value={value} onChange={(e) => setValue(e.target.value)} rows={4} />`}
            >
                <div className="w-full max-w-md space-y-4">
                    <Textarea
                        placeholder="Enter your message here..."
                        value={textareaValue}
                        onChange={(e) => setTextareaValue(e.target.value)}
                        rows={4}
                    />
                    {textareaValue && (
                        <p className="text-sm text-muted-foreground">
                            Character count: {textareaValue.length}
                        </p>
                    )}
                    <Textarea placeholder="Disabled textarea" disabled rows={3} />
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Slider */}
            <Section
                title="Slider"
                description="Range slider for selecting numeric values. Features brutal track styling and mint range indicator."
                codeExample={`<Slider 
  value={[value]} 
  onValueChange={(vals) => setValue(vals[0])}
  min={0}
  max={100}
  step={1}
/>`}
            >
                <div className="w-full max-w-md space-y-4">
                    <div>
                        <div className="flex justify-between mb-2">
                            <label className="text-sm font-medium">Volume</label>
                            <span className="text-sm text-muted-foreground">{sliderValue[0]}%</span>
                        </div>
                        <Slider
                            value={sliderValue}
                            onValueChange={setSliderValue}
                            min={0}
                            max={100}
                            step={1}
                        />
                    </div>
                    <div>
                        <div className="flex justify-between mb-2">
                            <label className="text-sm font-medium">Range</label>
                            <span className="text-sm text-muted-foreground">0 - 100</span>
                        </div>
                        <Slider defaultValue={[25, 75]} min={0} max={100} step={1} />
                    </div>
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Spinner */}
            <Section
                title="Spinner"
                description="Loading spinner with accessible labeling. Use when content is being fetched or processed."
                codeExample={`<Spinner />
<Spinner size="sm" />
<Spinner size="lg" />`}
            >
                <div className="flex items-center gap-6">
                    <div className="flex flex-col items-center gap-2">
                        <Spinner size="sm" />
                        <span className="text-xs text-muted-foreground">Small</span>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                        <Spinner size="md" />
                        <span className="text-xs text-muted-foreground">Medium</span>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                        <Spinner size="lg" />
                        <span className="text-xs text-muted-foreground">Large</span>
                    </div>
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Alert */}
            <Section
                title="Alert"
                description="Alert boxes for important messages. Supports info, success, warning, and error variants with brutal styling."
                codeExample={`<Alert variant="info">
  <AlertTitle>Info</AlertTitle>
  <AlertDescription>This is an informational message.</AlertDescription>
</Alert>

<Alert variant="success">
  <AlertTitle>Success!</AlertTitle>
  <AlertDescription>Operation completed successfully.</AlertDescription>
</Alert>`}
            >
                <div className="space-y-4 max-w-2xl">
                    <Alert variant="default">
                        <AlertTitle>Default Alert</AlertTitle>
                        <AlertDescription>
                            This is a default alert with standard styling.
                        </AlertDescription>
                    </Alert>
                    <Alert variant="info">
                        <AlertTitle>Information</AlertTitle>
                        <AlertDescription>
                            This is an informational message using the sky color variant.
                        </AlertDescription>
                    </Alert>
                    <Alert variant="success">
                        <AlertTitle>Success!</AlertTitle>
                        <AlertDescription>
                            Your action was completed successfully.
                        </AlertDescription>
                    </Alert>
                    <Alert variant="warning">
                        <AlertTitle>Warning</AlertTitle>
                        <AlertDescription>
                            Please review this information before proceeding.
                        </AlertDescription>
                    </Alert>
                    <Alert variant="error">
                        <AlertTitle>Error</AlertTitle>
                        <AlertDescription>
                            Something went wrong. Please try again.
                        </AlertDescription>
                    </Alert>
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Popover */}
            <Section
                title="Popover"
                description="Popover component for displaying additional content in a floating panel. Similar to tooltip but can contain more content."
                codeExample={`<Popover>
  <PopoverTrigger asChild>
    <Button>Open Popover</Button>
  </PopoverTrigger>
  <PopoverContent>
    <p>Popover content goes here</p>
  </PopoverContent>
</Popover>`}
            >
                <Popover>
                    <PopoverTrigger asChild>
                        <Button variant="outline">Open Popover</Button>
                    </PopoverTrigger>
                    <PopoverContent>
                        <div className="space-y-2">
                            <h4 className="font-semibold">Popover Title</h4>
                            <p className="text-sm text-muted-foreground">
                                This is popover content. It can contain any React elements.
                            </p>
                        </div>
                    </PopoverContent>
                </Popover>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Accordion */}
            <Section
                title="Accordion"
                description="Collapsible content sections. Click headers to expand or collapse content with smooth animations."
                codeExample={`<Accordion type="single" collapsible>
  <AccordionItem value="item-1">
    <AccordionTrigger>Is it accessible?</AccordionTrigger>
    <AccordionContent>
      Yes. It adheres to the WAI-ARIA design pattern.
    </AccordionContent>
  </AccordionItem>
</Accordion>`}
            >
                <Accordion type="single" collapsible className="w-full max-w-2xl">
                    <AccordionItem value="item-1">
                        <AccordionTrigger>What is this component library?</AccordionTrigger>
                        <AccordionContent>
                            This is a comprehensive UI component library built with Radix UI primitives and Tailwind CSS, styled with a neo-brutalist design system.
                        </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="item-2">
                        <AccordionTrigger>Is it accessible?</AccordionTrigger>
                        <AccordionContent>
                            Yes. All components use Radix UI primitives which adhere to WAI-ARIA design patterns and provide full keyboard navigation support.
                        </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="item-3">
                        <AccordionTrigger>Can I customize the colors?</AccordionTrigger>
                        <AccordionContent>
                            Yes! All components use CSS variables defined in src/index.css. You can override these tokens or use the color props available on many components.
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Table */}
            <Section
                title="Table"
                description="Table components for displaying structured data. Includes header, body, footer, and caption sections with brutal grid styling."
                codeExample={`<Table>
  <TableHeader>
    <TableRow>
      <TableHead>Name</TableHead>
      <TableHead>Status</TableHead>
      <TableHead>Role</TableHead>
    </TableRow>
  </TableHeader>
  <TableBody>
    <TableRow>
      <TableCell>John Doe</TableCell>
      <TableCell>Active</TableCell>
      <TableCell>Admin</TableCell>
    </TableRow>
  </TableBody>
</Table>`}
            >
                <div className="w-full max-w-2xl">
                    <Table>
                        <TableCaption>A list of users and their roles.</TableCaption>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Role</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow>
                                <TableCell className="font-medium">John Doe</TableCell>
                                <TableCell><Badge variant="success">Active</Badge></TableCell>
                                <TableCell>Admin</TableCell>
                                <TableCell className="text-right">
                                    <Button size="sm" variant="outline">Edit</Button>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-medium">Jane Smith</TableCell>
                                <TableCell><Badge variant="success">Active</Badge></TableCell>
                                <TableCell>User</TableCell>
                                <TableCell className="text-right">
                                    <Button size="sm" variant="outline">Edit</Button>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-medium">Bob Johnson</TableCell>
                                <TableCell><Badge variant="warning">Pending</Badge></TableCell>
                                <TableCell>User</TableCell>
                                <TableCell className="text-right">
                                    <Button size="sm" variant="outline">Edit</Button>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell colSpan={3} className="font-medium">Total Users</TableCell>
                                <TableCell className="text-right font-medium">3</TableCell>
                            </TableRow>
                        </TableFooter>
                    </Table>
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Pagination */}
            <Section
                title="Pagination"
                description="Pagination component for navigating through pages of content. Built with Button components and includes ellipsis for large page counts."
                codeExample={`<Pagination
  currentPage={currentPage}
  totalPages={10}
  onPageChange={setCurrentPage}
  showFirstLast={true}
/>`}
            >
                <div className="space-y-6">
                    <div>
                        <p className="text-sm text-muted-foreground mb-3">Standard pagination</p>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={10}
                            onPageChange={setCurrentPage}
                        />
                        <p className="mt-2 text-sm text-muted-foreground">
                            Current page: <strong>{currentPage}</strong> of 10
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground mb-3">With first/last buttons</p>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={10}
                            onPageChange={setCurrentPage}
                            showFirstLast={true}
                        />
                    </div>
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Breadcrumb */}
            <Section
                title="Breadcrumb"
                description="Breadcrumb navigation for showing the current page location in a hierarchy. Supports links and separators."
                codeExample={`<Breadcrumb>
  <BreadcrumbList>
    <BreadcrumbItem>
      <BreadcrumbLink href="/">Home</BreadcrumbLink>
    </BreadcrumbItem>
    <BreadcrumbSeparator />
    <BreadcrumbItem>
      <BreadcrumbLink href="/docs">Docs</BreadcrumbLink>
    </BreadcrumbItem>
    <BreadcrumbSeparator />
    <BreadcrumbItem>
      <BreadcrumbPage>Components</BreadcrumbPage>
    </BreadcrumbItem>
  </BreadcrumbList>
</Breadcrumb>`}
            >
                <div className="space-y-4">
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink href="#" onClick={(e) => e.preventDefault()}>Home</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbLink href="#" onClick={(e) => e.preventDefault()}>Components</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbPage>UI Elements</BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                    <div>
                        <p className="text-sm text-muted-foreground mb-2">With ellipsis (for long paths):</p>
                        <Breadcrumb>
                            <BreadcrumbList>
                                <BreadcrumbItem>
                                    <BreadcrumbLink href="#" onClick={(e) => e.preventDefault()}>Home</BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbEllipsis />
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbLink href="#" onClick={(e) => e.preventDefault()}>Deep</BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbPage>Nested Page</BreadcrumbPage>
                                </BreadcrumbItem>
                            </BreadcrumbList>
                        </Breadcrumb>
                    </div>
                </div>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Toast */}
            <Section
                title="Toast"
                description="Toast notifications for temporary messages. Automatically dismisses after a duration. Wrap your app with ToastProvider. Choose position to see different slide animations and swipe directions."
                codeExample={`<ToastProvider position={position} duration={5000}>
  <ToastViewport position={position} />
  <Toast variant="success" open={open} onOpenChange={setOpen} position={position}>
    <ToastTitle>Success!</ToastTitle>
    <ToastDescription>Operation completed.</ToastDescription>
    <ToastAction altText="Undo">Undo</ToastAction>
  </Toast>
</ToastProvider>`}
            >
                <ToastProvider position={toastPosition} duration={5000}>
                    <ToastViewport position={toastPosition} />
                    <div className="space-y-4">
                        <div className="flex items-center gap-4 flex-wrap">
                            <p className="text-sm text-muted-foreground">
                                Click buttons below to show different toast variants. Toasts auto-dismiss after 5 seconds.
                            </p>
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-medium">Position:</label>
                                <Select value={toastPosition} onValueChange={(value) => setToastPosition(value as ToastPosition)}>
                                    <SelectTrigger className="w-[140px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="top-center">Top Center</SelectItem>
                                        <SelectItem value="top-right">Top Right</SelectItem>
                                        <SelectItem value="bottom-right">Bottom Right</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="default"
                                onClick={() => {
                                    const id = Math.random().toString(36).substr(2, 9);
                                    setToasts([...toasts, { id, title: "Default Toast", description: "This is a default toast message.", variant: "default", open: true }]);
                                }}
                            >
                                Show Default
                            </Button>
                            <Button
                                variant="sky"
                                onClick={() => {
                                    const id = Math.random().toString(36).substr(2, 9);
                                    setToasts([...toasts, { id, title: "Info", description: "This is an informational toast.", variant: "info", open: true }]);
                                }}
                            >
                                Show Info
                            </Button>
                            <Button
                                variant="default"
                                bgColor="rgb(var(--ui-status-completed))"
                                onClick={() => {
                                    const id = Math.random().toString(36).substr(2, 9);
                                    setToasts([...toasts, { id, title: "Success!", description: "Operation completed successfully.", variant: "success", open: true }]);
                                }}
                            >
                                Show Success
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={() => {
                                    const id = Math.random().toString(36).substr(2, 9);
                                    setToasts([...toasts, { id, title: "Error", description: "Something went wrong.", variant: "error", open: true }]);
                                }}
                            >
                                Show Error
                            </Button>
                        </div>
                        {toasts.map((toast) => (
                            <Toast
                                key={toast.id}
                                variant={toast.variant || "default"}
                                position={toastPosition}
                                open={toast.open}
                                onOpenChange={(open) => {
                                    if (!open) {
                                        setToasts(toasts.filter(t => t.id !== toast.id));
                                    } else {
                                        setToasts(toasts.map(t => t.id === toast.id ? { ...t, open: true } : t));
                                    }
                                }}
                            >
                                <ToastTitle>{toast.title}</ToastTitle>
                                {toast.description && <ToastDescription>{toast.description}</ToastDescription>}
                                <ToastAction altText="Close" onClick={() => setToasts(toasts.filter(t => t.id !== toast.id))}>
                                    Close
                                </ToastAction>
                            </Toast>
                        ))}
                    </div>
                </ToastProvider>
            </Section>

            <hr className="border-t-2 border-dashed border-ink/30" />

            {/* Design Tokens */}
            <Section
                title="Design Tokens"
                description="All components use shared CSS variables defined in src/index.css. These tokens ensure consistency across the entire application."
                codeExample={`/* Colors */
--ui-ink: 35 35 35        /* Primary text/border color */
--ui-paper: 255 255 255   /* Background color */
--ui-mint: 96 250 193     /* Primary accent */
--ui-sky: 34 214 255      /* Secondary accent */
--ui-lemon: 255 222 52    /* Yellow accent */
--ui-pink: 250 96 137     /* Destructive accent */
--ui-gold: 255 233 93     /* Stats box color */

/* Shadows */
--ui-shadow-sm: 3px 3px 0px 0px rgba(0,0,0,0.6)
--ui-shadow-md: 5px 10px 0px 0px rgba(0,0,0,0.7)
--ui-shadow-lg: 8px 8px 0px 0px rgba(0,0,0,0.8)

/* Borders */
--ui-border-width: 1.5px
--ui-radius-sm: 1px`}
            >
                <Card>
                    <CardHeader>
                        <CardTitle>Available Tokens</CardTitle>
                        <CardDescription>
                            These CSS variables are used throughout all components for consistent theming.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                                <h4 className="font-semibold mb-2">Colors</h4>
                                <ul className="space-y-1 text-muted-foreground">
                                    <li>• ink, paper, muted</li>
                                    <li>• mint, sky, lemon, pink, gold</li>
                                    <li>• status-* (completed, generating, etc.)</li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-semibold mb-2">Shadows & Borders</h4>
                                <ul className="space-y-1 text-muted-foreground">
                                    <li>• shadow-brutal-sm, -md, -lg</li>
                                    <li>• shadow-inset, shadow-inset-soft</li>
                                    <li>• border-brutal, border-brutal-thick</li>
                                </ul>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </Section>
        </div>
    );
};

export default StyleGuide;
